import { Lead, LeadStats, LeadFilters } from '../types/Lead';
import { firestoreLeadService } from './firestoreLeadService';

export const loadLeads = async (): Promise<Lead[]> => {
  try {
    // Load from Firestore
    console.log('🔥 Loading leads from Firestore...');
    const leads = await firestoreLeadService.loadLeads();
    console.log(`✅ Successfully loaded ${leads.length} leads from Firestore`);
    return leads;
  } catch (error) {
    console.error('❌ Failed to load leads from Firestore:', error);
    throw new Error('Failed to load leads from Firestore');
  }
};

export const calculateStats = (leads: Lead[]): LeadStats => {
  const totalLeads = leads.length;
  const topLeads = leads.filter(lead => lead.processed.averageScore > 7).length;

  const companySizeSet = new Set(leads.map(lead => lead.metadata.companySize.split(' ')[0]));
  const sectorSet = new Set(leads.map(lead => lead.metadata.sector));
  const geographySet = new Set(leads.map(lead => lead.metadata.hqCountry));

  const companySizes = Array.from(companySizeSet).sort();
  const sectors = Array.from(sectorSet).sort();
  const geographies = Array.from(geographySet).sort();

  return {
    totalLeads,
    topLeads,
    companySizes,
    sectors,
    geographies
  };
};

/**
 * Filter leads by the selected filter values.
 *
 * Within a single filter the selected values are combined with OR (a lead
 * matches if its value is included in that filter's selected array). Different
 * filters are combined with AND. An empty or absent array means that filter is
 * inactive and matches every lead.
 */
export const filterLeads = (leads: Lead[], filters: LeadFilters): Lead[] => {
  const { companySize, sector, geography } = filters;

  return leads.filter(lead => {
    const matchesCompanySize =
      !companySize || companySize.length === 0 ||
      companySize.some(size => lead.metadata.companySize.startsWith(size));
    const matchesSector =
      !sector || sector.length === 0 ||
      sector.includes(lead.metadata.sector);
    const matchesGeography =
      !geography || geography.length === 0 ||
      geography.includes(lead.metadata.hqCountry);
    return matchesCompanySize && matchesSector && matchesGeography;
  });
};

/**
 * Force load leads from Firestore (bypass cache)
 */
export const loadLeadsFromFirestore = async (): Promise<Lead[]> => {
  firestoreLeadService.clearCache();
  return await firestoreLeadService.loadLeads();
};

/**
 * Force load leads from local files
 */
export const loadLeadsFromLocalFiles = async (): Promise<Lead[]> => {
  throw new Error('Local file loading is no longer supported. Please use Firestore.');
};

/**
 * Get a single lead by ID (from Firestore)
 */
export const getLeadById = async (id: string): Promise<Lead | null> => {
  try {
    return await firestoreLeadService.getLeadById(id);
  } catch (error) {
    console.error(`❌ Error getting lead ${id}:`, error);
    return null;
  }
};

/**
 * Search leads by company name
 */
export const searchLeadsByCompany = async (searchTerm: string): Promise<Lead[]> => {
  try {
    return await firestoreLeadService.searchLeadsByCompany(searchTerm);
  } catch (error) {
    console.warn('⚠️ Firestore search failed, falling back to local search');
    const allLeads = await firestoreLeadService.loadLeads(); // Assuming loadLeads is the source for search
    return allLeads.filter(lead =>
      lead.metadata.companyName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
};



/**
 * Get data source information
 */
export const getDataSourceInfo = async (): Promise<{
  source: 'firestore';
  leadCount: number;
  cacheStatus?: any;
}> => {
  try {
    const leads = await firestoreLeadService.loadLeads();
    return {
      source: 'firestore',
      leadCount: leads.length,
      cacheStatus: firestoreLeadService.getCacheStatus()
    };
  } catch (error) {
    throw new Error('Failed to get data source info from Firestore');
  }
};

// Note: Members are now managed separately via memberServiceV2

/**
 * Update a lead in Firestore
 */
export const updateLead = async (leadId: string, updates: Partial<Lead>): Promise<void> => {
  return await firestoreLeadService.updateLead(leadId, updates);
};

/**
 * Get a lead from cache by ID
 */
export const getLeadFromCache = (leadId: string): Lead | null => {
  return firestoreLeadService.getLeadFromCache(leadId);
};